/**
 * His particulars on the case file (Phase A, A9, 32.7, 32.9) — and where they
 * are corrected.
 *
 * Four things are the product rather than the layout:
 *
 * **One place, shown everywhere.** Name, ticket, post and section are corrected
 * here, each with its reason, and the case header and every later draft read
 * the corrected value. His record is shared by his cases, so a correction made
 * here reaches the others too — and the block says which.
 *
 * **The old value stays beneath** (rule 4). Under each value: what the app read
 * at intake, struck, where it differs; and every correction, oldest first, with
 * who made it, when and why.
 *
 * **The papers' own wording is offered, not typed.** An officer's keyboard is
 * English; the document's words are the paper's. Each value the papers give —
 * and the page's own wording for one the checks threw out — is a chip to pick.
 *
 * **The gist is shown, never edited** (A8): no document prints it.
 *
 * The labels, the values, the history and whether a correction is possible all
 * come off the API; a greyed Correct says why beside it.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { casePageApi } from '../../api/client'
import type {
  CaseDetail,
  CorrectParticularBody,
  IntakeEnglishView,
  ParticularChangeView,
  ParticularFieldView,
  ParticularsView,
} from '../../api/client'
import { ErrorEnvelope } from '../../components/bits'
import { readDay } from '../../lib/blocks'
import { isCourtDocument } from '../../lib/court'
import { readDate } from '../../lib/period'
import { useRefreshCase } from './acting'
import './case-page.css'

type FieldKey = ParticularFieldView['field']

/** His other cases, by file number — his record is shared with them. */
function othersOf(view: ParticularsView): string[] {
  return view.other_cases ?? []
}

/** What the intake's reference readings call each, for a case confirmed before
 *  English was settled at intake — readings, labelled as readings. */
const READING_OF: Partial<Record<FieldKey, keyof IntakeEnglishView>> = {
  name: 'name_en',
  designation: 'designation_en',
  section: 'section_en',
}

export function Particulars({
  data,
  readings,
  readOnly,
}: {
  data: CaseDetail
  readings: IntakeEnglishView | null
  readOnly: boolean
}) {
  const view = useQuery({
    queryKey: ['case', data.id, 'particulars'],
    queryFn: () => casePageApi.getParticulars(data.id),
  })
  /** The field a "Needs a look" line pointed at, by the page's hash. */
  const [wanted, setWanted] = useState<FieldKey | null>(null)
  useEffect(() => {
    const read = () => {
      const match = /^#particular-(name|ticket|designation|section)$/.exec(window.location.hash)
      setWanted(match ? (match[1] as FieldKey) : null)
    }
    read()
    window.addEventListener('hashchange', read)
    return () => window.removeEventListener('hashchange', read)
  }, [])

  const particulars = view.data ?? null
  // A court case has no complaint, and its replies print no subject line, so
  // the line is not drawn on it (Archit, 5 Oct 2026: "remove subject").
  const atCourt =
    isCourtDocument(data.declared_reached) || isCourtDocument(data.next_action?.document)
  return (
    <div className="cf-block" data-testid="cf-particulars" id="his-particulars">
      <h4>His particulars</h4>
      <dl className="cf-dl">
        {particulars ? (
          particulars.fields.map((field) => (
            <FieldRow
              key={field.field}
              data={data}
              view={particulars}
              field={field}
              reading={
                READING_OF[field.field] && readings
                  ? ((readings[READING_OF[field.field] as keyof IntakeEnglishView] as
                      string | null | undefined) ?? null)
                  : null
              }
              readOnly={readOnly}
              wanted={wanted === field.field}
              onSettled={() => setWanted(null)}
            />
          ))
        ) : (
          <Fallback data={data} readings={readings} />
        )}
        <dt>Opened</dt>
        <dd>
          {readDate(data.opened_on)}
          {data.historical_note ? <span className="sub"> — {data.historical_note}</span> : null}
        </dd>
        {/* The subject every document prints as its heading line — shown, so
            the line on a served instrument is never one nobody on the case
            page could read back. Unsettled, it says what the documents do. */}
        {atCourt ? null : (
          <>
            <dt>Subject</dt>
            <dd className="prose" data-testid="cf-subject">
              {data.subject_hi ? (
                <>
                  {data.subject_en ?? <span className="deva">{data.subject_hi}</span>}
                  {data.subject_en ? (
                    <span className="sub deva"> {data.subject_hi}</span>
                  ) : null}
                </>
              ) : data.subject_hi_read ? (
                <>
                  <span className="deva">{data.subject_hi_read}</span>
                  <span className="sub"> — read off the complaint; each draft prints it</span>
                </>
              ) : (
                <span className="warn-text">
                  Not read off the complaint — the documents leave it as a blank to fill.
                </span>
              )}
            </dd>
          </>
        )}
        <Gist data={data} view={particulars} />
      </dl>
      {particulars && othersOf(particulars).length > 0 ? (
        <p className="small muted particulars-shared" data-testid="particulars-shared">
          His record is shared with his other case
          {othersOf(particulars).length === 1 ? '' : 's'},{' '}
          <span className="mono">{othersOf(particulars).join(', ')}</span>. A correction made
          here reaches {othersOf(particulars).length === 1 ? 'it' : 'them'} too.
        </p>
      ) : null}
      {particulars && !particulars.correctable && !readOnly ? (
        <p className="small muted" data-testid="particulars-not-correctable">
          {particulars.not_correctable_reason}
        </p>
      ) : null}
      {view.error ? <ErrorEnvelope error={view.error} /> : null}
    </div>
  )
}

/** The gist: the case's one line, shown and never edited (A8). */
function Gist({ data, view }: { data: CaseDetail; view: ParticularsView | null }) {
  const gist = view?.gist ?? data.gist_en ?? data.gist_hi ?? null
  if (!gist) return null
  return (
    <>
      <dt>Gist</dt>
      <dd className="prose" data-testid="cf-gist">
        <span className={/[\u0900-\u097F]/.test(gist) ? 'deva' : undefined}>{gist}</span>
        <span className="sub">
          {' '}
          — {view?.gist_from ? `${view.gist_from} ` : ''}Shown for reference; no document prints
          it, so it is not edited.
        </span>
      </dd>
    </>
  )
}

/** While his particulars load: the case's own copy, so the block is never empty. */
function Fallback({
  data,
  readings,
}: {
  data: CaseDetail
  readings: IntakeEnglishView | null
}) {
  const employee = data.employee as Record<string, string | null>
  const nameEn = (employee['name_en'] ?? '').trim() || readings?.name_en || null
  return (
    <>
      <dt>Name</dt>
      <dd>{nameEn ?? <span className="deva">{employee['name_hi'] ?? '—'}</span>}</dd>
      <dt>Ticket</dt>
      <dd className="mono">{employee['staff_number'] ?? 'not on the record'}</dd>
      <dt>Post</dt>
      <dd>
        {(employee['designation_en'] ?? '').trim() ||
          readings?.designation_en ||
          employee['designation_hi'] ||
          'not on the record'}
      </dd>
      <dt>Section</dt>
      <dd>
        {(employee['section_en'] ?? '').trim() ||
          readings?.section_en ||
          employee['section_hi'] ||
          'not on the record'}
      </dd>
    </>
  )
}

function FieldRow({
  data,
  view,
  field,
  reading,
  readOnly,
  wanted,
  onSettled,
}: {
  data: CaseDetail
  view: ParticularsView
  field: ParticularFieldView
  reading: string | null
  readOnly: boolean
  wanted: boolean
  /** The line that pointed here has been dealt with: stop pointing. */
  onSettled: () => void
}) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (wanted && view.correctable && !readOnly) setOpen(true)
  }, [wanted, view.correctable, readOnly])

  const english = (field.english ?? '').trim() || null
  const value = (field.value ?? '').trim() || null
  const mono = field.field === 'ticket'
  return (
    <>
      <dt id={`particular-${field.field}`}>{field.label}</dt>
      <dd
        className={`particular-row${wanted ? ' wanted' : ''}`}
        data-testid={`particular-${field.field}`}
      >
        <div className="particular-value">
          {english ? (
            <span>{english}</span>
          ) : value ? null : reading ? (
            <span>
              {reading} <span className="sub">— a reading of the Hindi, never settled</span>
            </span>
          ) : null}
          {value ? (
            <span className={english ? 'sub deva' : mono ? 'mono' : 'deva'}>{value}</span>
          ) : null}
          {!english && !value && !reading ? (
            <span className="muted">not on the record</span>
          ) : null}
          {readOnly ? null : (
            <span className="particular-acts">
              <button
                type="button"
                className="btn ghost"
                disabled={!view.correctable || open}
                title={
                  view.correctable ? undefined : (view.not_correctable_reason ?? undefined)
                }
                onClick={() => setOpen(true)}
                data-testid={`correct-${field.field}`}
              >
                Correct
              </button>
            </span>
          )}
        </div>
        {field.machine && field.machine !== field.value ? (
          <div className="detected" data-testid={`machine-${field.field}`}>
            Read by the app at intake: <span className="what deva">{field.machine}</span>
          </div>
        ) : null}
        {(field.history ?? []).map((change) => (
          <Change key={change.entry_id} change={change} caseId={data.id} />
        ))}
        {open ? (
          <CorrectForm
            data={data}
            view={view}
            field={field}
            onDone={() => {
              setOpen(false)
              if (window.location.hash === `#particular-${field.field}`) {
                window.history.replaceState(null, '', window.location.pathname)
              }
              onSettled()
            }}
          />
        ) : null}
      </dd>
    </>
  )
}

function Change({ change, caseId }: { change: ParticularChangeView; caseId: string }) {
  const was =
    [change.was_value, change.was_english].filter(Boolean).join(' · ') || 'not on the record'
  return (
    <div className="was" data-testid={`was-${change.entry_id}`}>
      <span className="deva">was {was}</span> — <span className="who">corrected</span>{' '}
      {readDate(readDay(change.at))}
      {change.by ? ` by ${change.by}` : ''}
      {change.case_id !== caseId && change.file_number ? (
        <>
          {' '}
          on <span className="mono">{change.file_number}</span>
        </>
      ) : null}
      : {change.reason}
    </div>
  )
}

function CorrectForm({
  data,
  view,
  field,
  onDone,
}: {
  data: CaseDetail
  view: ParticularsView
  field: ParticularFieldView
  onDone: () => void
}) {
  const refresh = useRefreshCase(data.id)
  const [value, setValue] = useState(field.value ?? '')
  const [english, setEnglish] = useState(field.english ?? '')
  const [reason, setReason] = useState('')
  const valueChanged = value.trim() !== (field.value ?? '').trim()
  const englishChanged = field.has_english && english.trim() !== (field.english ?? '').trim()
  const changed = valueChanged || englishChanged

  const save = useMutation({
    mutationFn: () => {
      const body: CorrectParticularBody = {
        preflight: false,
        field: field.field,
        reason: reason.trim(),
        value: valueChanged ? value.trim() : null,
        english: englishChanged ? english.trim() : null,
      }
      return casePageApi.correctParticular(data.id, body)
    },
    onSuccess: () => {
      refresh()
      onDone()
    },
  })

  const why = !reason.trim()
    ? 'Every correction records its reason.'
    : !changed
      ? 'Nothing has been changed yet.'
      : null
  const words = field.label.toLowerCase()
  return (
    <div className="particular-form" data-testid={`correct-form-${field.field}`}>
      {(field.from_papers ?? []).length > 0 ? (
        <div className="picks">
          <span>As the papers write it:</span>
          {(field.from_papers ?? []).map((pick) => (
            <button
              key={pick.value}
              type="button"
              className="pick"
              title={
                pick.page_wording
                  ? 'The page’s own wording, found by the checks where the reading did not copy it exactly.'
                  : pick.unclear
                    ? 'The paper hedges this value, or the scan made it unsure.'
                    : undefined
              }
              onClick={() => {
                setValue(pick.value)
                if (field.has_english && pick.english) setEnglish(pick.english)
              }}
            >
              <span className={field.field === 'ticket' ? 'mono' : 'deva'}>{pick.value}</span>
              {pick.english && pick.english !== pick.value ? ` · ${pick.english}` : ''}
              <span className="where">
                {(pick.where ?? []).join('; ')}
                {pick.page_wording ? ' · the page’s wording' : ''}
                {pick.unclear ? ' · unclear' : ''}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {field.has_english ? (
        <label>
          In English — the screens and English documents
          <input
            aria-label={`His ${words} in English`}
            value={english}
            onChange={(event) => setEnglish(event.target.value)}
          />
        </label>
      ) : null}
      <label>
        {field.has_english ? 'As the documents print it' : `His ${words}`}
        <input
          aria-label={
            field.has_english ? `His ${words} as the documents print it` : `His ${words}`
          }
          className={field.field === 'ticket' ? 'mono' : 'deva'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      </label>
      <label>
        Why
        <input
          aria-label="Why it is corrected"
          value={reason}
          placeholder="the service record gives it so"
          onChange={(event) => setReason(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onDone()
          }}
        />
      </label>
      <p className="small muted">
        Every draft made from now on uses it. Drafts already made keep what they said and are
        marked; issued documents stay as issued.
        {othersOf(view).length
          ? ` His other case${othersOf(view).length === 1 ? '' : 's'} (${othersOf(view).join(', ')}) will have it too.`
          : ''}
      </p>
      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={Boolean(why) || save.isPending}
          title={why ?? undefined}
          onClick={() => save.mutate()}
          data-testid={`save-${field.field}`}
        >
          {save.isPending ? 'Saving…' : 'Save the correction'}
        </button>
        <button type="button" className="btn ghost" disabled={save.isPending} onClick={onDone}>
          Cancel
        </button>
      </div>
      {why && !save.isPending ? <p className="small muted">{why}</p> : null}
      <ErrorEnvelope error={save.error} />
    </div>
  )
}
